import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { LogoMark, BrandLockup } from "@/components/LogoMark";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "rounded-none focus:ring-2 focus:ring-blue-800";
const btnCls = "w-full rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11 transition-colors duration-200";

export default function Login() {
  const { login, loginWithOtp, register, logout } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | otp | register
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "advocate", phone: "" });
  const [otp, setOtp] = useState({ channel: "sms", identifier: "", code: "", requested: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [forgot, setForgot] = useState({ email: "", sent: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setO = (k, v) => setOtp((o) => ({ ...o, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") await login(form.email, form.password);
      else if (mode === "admin") {
        const u = await login(form.email, form.password);
        if (u && u.role !== "owner") {
          await logout();
          throw new Error("This account is not an administrator. Use the Password tab to sign in.");
        }
        navigate("/registry");
        return;
      }
      else await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const requestOtp = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/otp/request", {
        identifier: otp.identifier, channel: otp.channel,
      });
      setO("requested", true);
      toast.success(`Passcode issued — ${data.delivery}`);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await loginWithOtp(otp.identifier, otp.code);
      navigate("/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/password/forgot", { email: forgot.email });
      setForgot((f) => ({ ...f, sent: data.message }));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block grain bg-slate-950">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/5c13d8a7-969e-4c7a-8c31-9cbdabb06a93/images/260feef2b61d325e65d2fd2ff888240ededa083b833f656da650e8cfe719bc73.jpeg"
          alt="Map of India with major districts marked, under the emblem of arbitration"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-slate-950/20" />
        <div className="relative z-10 flex h-full flex-col justify-end p-14">
          <div className="max-w-md">
            <div className="flex items-center gap-2.5 text-white">
              <LogoMark className="h-8 w-8" />
              <span className="font-display text-2xl">Adhoc<span className="text-amber-200">Arb</span></span>
            </div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-200/70 mt-6">Ad-hoc Arbitration · India</p>
            <h2 className="font-display text-4xl leading-tight text-white mt-4">
              Streamlined case management for ad-hoc arbitral references.
            </h2>
            <p className="text-sm text-slate-300 mt-5 leading-relaxed">
              Separate dashboards for the arbitrator, the advocates and the clients — with daily orders,
              adjournment requests, online fee payment and reminders before every sitting.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 sm:px-14">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10"><BrandLockup /></div>
          <p className="label-caps">Secure access</p>
          <h1 className="font-display text-3xl mt-2">
            {mode === "forgot" ? "Reset your password"
              : mode === "register" ? "Create your profile"
              : mode === "admin" ? "Administrator sign in"
              : mode === "otp" ? "Sign in with a passcode" : "Sign in"}
          </h1>

          <div className="mt-6 flex border border-border" data-testid="auth-mode-tabs">
            {[["login", "Password"], ["otp", "OTP"], ["register", "Register"], ["admin", "Admin"]].map(([m, l]) => (
              <button key={m} data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 py-2.5 text-xs uppercase tracking-widest transition-colors duration-200 ${
                  mode === m ? "bg-blue-900 text-white" : "hover:bg-accent"
                }`}>
                {l}
              </button>
            ))}
          </div>

          {mode === "forgot" ? (
            <form onSubmit={submitForgot} className="mt-8 space-y-5" data-testid="forgot-form">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Enter the email on your AdhocArb account. We will send you a link, valid for one hour,
                to set a new password.
              </p>
              <div className="space-y-2">
                <Label className="label-caps">Email</Label>
                <Input data-testid="forgot-email-input" type="email" required value={forgot.email}
                  onChange={(e) => setForgot((f) => ({ ...f, email: e.target.value, sent: "" }))}
                  className={inputCls} />
              </div>
              {error && <p className="text-sm text-red-800 dark:text-red-400" data-testid="login-error">{error}</p>}
              {forgot.sent && (
                <p className="text-sm text-blue-900 dark:text-blue-400" data-testid="forgot-success">{forgot.sent}</p>
              )}
              <Button type="submit" disabled={busy} data-testid="forgot-submit-button" className={btnCls}>
                {busy ? "Sending…" : "Send reset link"}
              </Button>
              <button type="button" data-testid="forgot-back-button"
                onClick={() => { setMode("login"); setError(""); }}
                className="text-xs uppercase tracking-widest text-blue-900 dark:text-blue-400">
                Back to sign in
              </button>
            </form>
          ) : mode === "otp" ? (
            <form onSubmit={submitOtp} className="mt-8 space-y-5" data-testid="otp-form">
              <div className="space-y-2">
                <Label className="label-caps">Send passcode by</Label>
                <Select value={otp.channel} onValueChange={(v) => setO("channel", v)}>
                  <SelectTrigger className="rounded-none" data-testid="otp-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sms">SMS to my mobile</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-caps">{otp.channel === "sms" ? "Mobile number" : "Email"}</Label>
                <Input data-testid="otp-identifier-input" required value={otp.identifier}
                  onChange={(e) => setO("identifier", e.target.value)} className={inputCls}
                  placeholder={otp.channel === "sms" ? "+91-9900000000" : "you@example.com"} />
              </div>
              {!otp.requested ? (
                <Button type="button" disabled={busy || !otp.identifier} data-testid="otp-request-button"
                  onClick={requestOtp} className={btnCls}>
                  {busy ? "Sending…" : "Send passcode"}
                </Button>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label className="label-caps">6-digit passcode</Label>
                    <Input data-testid="otp-code-input" required value={otp.code} maxLength={6}
                      onChange={(e) => setO("code", e.target.value)} className={`${inputCls} font-mono tracking-[0.4em]`} />
                  </div>
                  <Button type="submit" disabled={busy} data-testid="otp-verify-button" className={btnCls}>
                    {busy ? "Verifying…" : "Sign in"}
                  </Button>
                  <button type="button" data-testid="otp-resend-button" onClick={requestOtp}
                    className="text-xs uppercase tracking-widest text-blue-900 dark:text-blue-400">Resend passcode</button>
                </>
              )}
              {error && <p className="text-sm text-red-800 dark:text-red-400" data-testid="login-error">{error}</p>}
              <p className="text-[11px] text-muted-foreground">
                SMS passcodes are logged for now and go live as soon as Twilio credentials are added. Email passcodes are live.
              </p>
            </form>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="auth-form">
              {mode === "register" && (
                <>
                  <div className="space-y-2">
                    <Label className="label-caps">Full name</Label>
                    <Input data-testid="register-name-input" required value={form.name}
                      onChange={(e) => set("name", e.target.value)} className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">I am a</Label>
                    <Select value={form.role} onValueChange={(v) => set("role", v)}>
                      <SelectTrigger className="rounded-none" data-testid="register-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="arbitrator">Arbitrator</SelectItem>
                        <SelectItem value="advocate">Advocate</SelectItem>
                        <SelectItem value="client">Client / Party</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Mobile number</Label>
                    <Input data-testid="register-phone-input" value={form.phone} placeholder="+91"
                      onChange={(e) => set("phone", e.target.value)} className={inputCls} />
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label className="label-caps">Email</Label>
                <Input data-testid="login-email-input" type="email" required value={form.email}
                  onChange={(e) => set("email", e.target.value)} className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Password</Label>
                <div className="relative">
                  <Input data-testid="login-password-input" type={showPwd ? "text" : "password"} required
                    value={form.password} onChange={(e) => set("password", e.target.value)}
                    className={`${inputCls} pr-10`} />
                  <button type="button" data-testid="login-password-toggle"
                    onClick={() => setShowPwd((s) => !s)}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200">
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {mode === "login" && (
                <button type="button" data-testid="forgot-password-link"
                  onClick={() => { setMode("forgot"); setError(""); }}
                  className="text-xs uppercase tracking-widest text-blue-900 dark:text-blue-400">
                  Forgot password?
                </button>
              )}
              {error && <p className="text-sm text-red-800 dark:text-red-400" data-testid="login-error">{error}</p>}
              <Button type="submit" disabled={busy} data-testid="login-submit-button" className={btnCls}>
                {busy ? "Please wait…" : mode === "login" ? "Enter dashboard" : "Create profile"}
              </Button>
            </form>
          )}

          <p className="mt-10 text-[11px] text-muted-foreground">
            Access is restricted to the arbitrator, advocates and parties on record.
          </p>
        </div>
      </div>
    </div>
  );
}
